import React, { useState } from "react";
import { API_URL } from "../config";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Notification from "../components/Notification";

function PasswordReset() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ 
        type: "", 
        message: "", 
        isVisible: false 
    });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleCloseNotification = () => {
        setNotification({ ...notification, isVisible: false });
    };

    const validate = () => {
        const newErrors = {};
        let isValid = true;

        if (!password) {
            newErrors.password = "Password is required";
            isValid = false;
        } else if (password.length < 8) {
            newErrors.password = "Password must be at least 8 characters";
            isValid = false;
        }

        if (!passwordConfirmation) {
            newErrors.passwordConfirmation = "Please confirm your password";
            isValid = false;
        } else if (password !== passwordConfirmation) {
            newErrors.passwordConfirmation = "Passwords do not match";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validate()) return;

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/password/reset`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                    token, 
                    password, 
                    password_confirmation: passwordConfirmation 
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setNotification({
                    type: "success",
                    message: "Password reset successfully! Redirecting to login...",
                    isVisible: true,
                });
                setTimeout(() => navigate("/login"), 3000);
            } else {
                // Handle API validation errors
                const apiErrors = data.errors || {};
                setErrors({
                    password: apiErrors.password ? apiErrors.password[0] : "",
                    passwordConfirmation: apiErrors.password_confirmation 
                        ? apiErrors.password_confirmation[0] 
                        : ""
                });
                
                setNotification({
                    type: "danger",
                    message: data.message || "Failed to reset password",
                    isVisible: true,
                });
            }
        } catch (error) {
            setNotification({
                type: "danger",
                message: "An unexpected error occurred",
                isVisible: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Header />
            <main>
                <div className="signin-page mb-4">
                    <div className="signin-bg"></div>
                    <div className="signin-card">
                        <div className="signin-content">
                            <div className="media pb-4 mb-4 is-justify-content-space-between">
                                <div className="media-left">
                                    <Link to="/login">
                                        <i className="fa fa-arrow-left"></i> &nbsp; Back to Login
                                    </Link>
                                </div>
                                <div className="media-right">
                                    <Link to="/">
                                        <i className="fa-solid fa-x"></i>
                                    </Link>
                                </div>
                            </div>
                            <div className="content">
                                <h1 className="sign-in-title">Reset Password</h1>

                                {notification.isVisible && (
                                    <Notification
                                        type={notification.type}
                                        message={notification.message}
                                        onClose={handleCloseNotification}
                                    />
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className={`field ${errors.password ? "is-danger" : ""}`}>
                                        <label className="label">New Password</label>
                                        <div className="control has-icons-left">
                                            <input
                                                className={`input ${errors.password ? "is-danger" : ""}`}
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="Enter new password"
                                            />
                                            <span className="icon is-small is-left">
                                                <i className="fas fa-lock"></i>
                                            </span>
                                        </div>
                                        {errors.password && (
                                            <p className="help is-danger">{errors.password}</p>
                                        )}
                                    </div>

                                    <div className={`field ${errors.passwordConfirmation ? "is-danger" : ""}`}>
                                        <label className="label">Confirm New Password</label>
                                        <div className="control has-icons-left">
                                            <input
                                                className={`input ${errors.passwordConfirmation ? "is-danger" : ""}`}
                                                type="password"
                                                value={passwordConfirmation}
                                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                                                placeholder="Confirm new password"
                                            />
                                            <span className="icon is-small is-left">
                                                <i className="fas fa-lock"></i>
                                            </span>
                                        </div>
                                        {errors.passwordConfirmation && (
                                            <p className="help is-danger">{errors.passwordConfirmation}</p>
                                        )}
                                    </div>

                                    <div className="field">
                                        <div className="control">
                                            <button
                                                type="submit"
                                                className="button is-fullwidth primary-bg"
                                                disabled={loading}
                                            >
                                                {loading ? "Processing..." : "Reset Password"}
                                            </button>
                                        </div>
                                    </div>
                                </form>

                                <div className="field mb-5 mt-4">
                                    <div className="control">
                                        <label>
                                            Remember your password? &nbsp;
                                            <Link to="/login">Login here</Link>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default PasswordReset;